import { useState } from 'react';
import { StationSector } from '../../../../../../utils/types';

type StationFieldErrors = {
  [sectorIndex: number]: {
    [K in keyof StationSector]?: string | null;
  };
};

/**
 * Custom hook for station field validation
 * Tracks touched fields and error messages per sector
 */
export function useStationValidation() {
  const [fieldErrors, setFieldErrors] = useState<StationFieldErrors>({});
  const [touchedFields, setTouchedFields] = useState<{
    [sectorIndex: number]: Set<keyof StationSector>;
  }>({});

  /**
   * Validate a specific field in a station sector
   */
  const validateStationField = (
    sectorIndex: number,
    fieldName: keyof StationSector,
    value: StationSector[keyof StationSector]
  ): string | null => {
    switch (fieldName) {
      case 'departureStation':
      case 'arrivalStation':
        const stationCode = ((value as string) || '')
          .trim()
          .toUpperCase()
          .replace(/[^A-Z]/g, '');
        if (!stationCode) return 'Station code is required';
        if (stationCode.length !== 3) return 'Must be 3 letters';
        if (!/^[A-Z]{3}$/.test(stationCode)) return 'Must be uppercase letters';
        return null;

      case 'std':
      case 'sta':
        const time = (value as string)?.trim() || '';
        if (!time) return 'Time is required';
        if (!/^\d{4}$/.test(time)) return 'Must be 4 digits (e.g., 0715)';
        const hours = parseInt(time.slice(0, 2), 10);
        const minutes = parseInt(time.slice(2, 4), 10);
        if (hours > 23 || minutes > 59) return 'Invalid time (0000-2359)';
        return null;

      case 'utcLocalTimeVariationDep':
      case 'utcLocalTimeVariationArr':
        const utcVar = (value as string)?.trim() || '';
        if (!utcVar) return 'UTC variation is required';
        if (!/^[+-][0-1][0-9][0-5][0-9]$/.test(utcVar)) {
          return 'Format: ±hhmm (e.g., +0800)';
        }
        return null;

      case 'iataSubTypeCode':
        const aircraftType = (value as string)?.trim() || '';
        if (!aircraftType) return null; // optional
        if (aircraftType.length < 2 || aircraftType.length > 4) {
          return 'Must be 2-4 characters';
        }
        if (!/^[A-Z0-9]+$/i.test(aircraftType)) {
          return 'Must be alphanumeric (letters and numbers)';
        }
        return null;

      case 'aircraftOwner':
        const owner = (value as string)?.trim() || '';
        if (!owner) return 'Aircraft owner is required';
        return null;

      // Optional fields - no validation needed
      case 'passengerTerminalDep':
      case 'passengerTerminalArr':
      case 'cd':
      case 'ca':
      case 'tailNo':
      case 'aircraftConfiguration':
      case 'onwardFlight':
        return null;

      default:
        return null;
    }
  };

  /**
   * Check if departure and arrival are the same airport within a sector.
   * Returns the error string or null.
   */
  const checkSameAirport = (
    station: StationSector,
    fieldName: keyof StationSector,
    value: StationSector[keyof StationSector]
  ): string | null => {
    if (fieldName !== 'departureStation' && fieldName !== 'arrivalStation') return null;
    const dep = (fieldName === 'departureStation' ? value as string : station.departureStation || '').trim().toUpperCase();
    const arr = (fieldName === 'arrivalStation' ? value as string : station.arrivalStation || '').trim().toUpperCase();
    if (dep && arr && dep === arr) return 'Departure and arrival cannot be the same airport';
    return null;
  };

  /**
   * Validate and set error for a field
   */
  const validateAndSetError = (
    sectorIndex: number,
    fieldName: keyof StationSector,
    value: StationSector[keyof StationSector],
    station?: StationSector
  ) => {
    const error = validateStationField(sectorIndex, fieldName, value)
      || (station ? checkSameAirport(station, fieldName, value) : null);
    setFieldErrors(prev => ({
      ...prev,
      [sectorIndex]: {
        ...prev[sectorIndex],
        [fieldName]: error,
      },
    }));
    setTouchedFields(prev => {
      const sectorTouched = prev[sectorIndex] || new Set<keyof StationSector>();
      sectorTouched.add(fieldName);
      return {
        ...prev,
        [sectorIndex]: sectorTouched,
      };
    });
  };

  /**
   * Handle field blur - validate the field
   */
  const handleBlur = (
    sectorIndex: number,
    fieldName: keyof StationSector,
    value: StationSector[keyof StationSector],
    station?: StationSector
  ) => {
    validateAndSetError(sectorIndex, fieldName, value, station);
  };

  /**
   * Get error message for a field (only show if touched)
   */
  const getFieldError = (
    sectorIndex: number,
    fieldName: keyof StationSector
  ): string | null => {
    const sectorTouched = touchedFields[sectorIndex];
    if (!sectorTouched || !sectorTouched.has(fieldName)) return null;
    return fieldErrors[sectorIndex]?.[fieldName] || null;
  };

  /**
   * Real-time validation (called on change if field was touched)
   */
  const revalidateIfTouched = (
    sectorIndex: number,
    fieldName: keyof StationSector,
    value: StationSector[keyof StationSector],
    station?: StationSector
  ) => {
    const sectorTouched = touchedFields[sectorIndex];
    if (sectorTouched && sectorTouched.has(fieldName)) {
      const error = validateStationField(sectorIndex, fieldName, value)
        || (station ? checkSameAirport(station, fieldName, value) : null);
      setFieldErrors(prev => ({
        ...prev,
        [sectorIndex]: {
          ...prev[sectorIndex],
          [fieldName]: error,
        },
      }));
    }
  };

  /**
   * Validate all fields in all sectors (used when Save is clicked)
   * Returns true if there are any errors, false if validation passed
   */
  const validateAllStations = (stations: StationSector[]): boolean => {
    const fieldsToValidate: (keyof StationSector)[] = [
      'departureStation',
      'std',
      'utcLocalTimeVariationDep',
      'arrivalStation',
      'sta',
      'utcLocalTimeVariationArr',
      'iataSubTypeCode',
      'aircraftOwner',
    ];

    const newErrors: StationFieldErrors = {};
    const newTouchedFields: { [sectorIndex: number]: Set<keyof StationSector> } = {};
    let hasErrors = false;

    stations.forEach((station, sectorIndex) => {
      const sectorErrors: { [K in keyof StationSector]?: string | null } = {};
      const sectorTouched = new Set<keyof StationSector>();

      fieldsToValidate.forEach(fieldName => {
        const error = validateStationField(sectorIndex, fieldName, station[fieldName]);
        sectorErrors[fieldName] = error;
        if (error) hasErrors = true;
        sectorTouched.add(fieldName);
      });

      // Cross-field: departure and arrival must not be the same airport
      const dep = (station.departureStation || '').trim().toUpperCase();
      const arr = (station.arrivalStation || '').trim().toUpperCase();
      if (dep && arr && dep === arr) {
        const sameAirportError = 'Departure and arrival cannot be the same airport';
        sectorErrors['arrivalStation'] = sameAirportError;
        hasErrors = true;
      }

      newErrors[sectorIndex] = sectorErrors;
      newTouchedFields[sectorIndex] = sectorTouched;
    });

    // Replace full error/touched state to avoid stale errors persisting after corrections.
    setFieldErrors(newErrors);
    setTouchedFields(newTouchedFields);

    return hasErrors;
  };

  /**
   * Reset validation state (used when modal closes/opens)
   */
  const resetValidation = () => {
    setFieldErrors({});
    setTouchedFields({});
  };

  return {
    handleBlur,
    getFieldError,
    revalidateIfTouched,
    validateAllStations,
    resetValidation,
  };
}
